import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronDown, Sun, Moon, Monitor, Gauge, LayoutDashboard, CalendarClock, Package, ClipboardList } from "lucide-react";
import { AuthContext } from "@/App";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MAINT_STAGES, MAINT_EXTRAS } from "@/components/maintenance/lib";

// Maintenance shell — the maintenance team's whole working surface, and the
// breakdown-reporting surface for production. A sibling app to the ERP
// <Layout> (cloned from StockLayout): it does NOT load the ERP data contexts,
// but wears the same header anatomy and the same Dashboard + chevron-pipeline
// nav row, so it reads as ONE product. See MAINTENANCE_MODULE_PLAN.md §8.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// NOTE: never pass a per-request auth config here. The app sets
// axios.defaults.headers.common.Authorization once at login and on every
// reload, from localStorage key `rl_token`. A hand-rolled config overrides
// that header — and reading the wrong key sends "Bearer null", which the
// global 401 interceptor turns into a silent sign-out.

// ── Module context — config, capability flags, tab counts and one refresh
// signal every surface listens to (a ticket action anywhere re-reads the nav).
export const MaintenanceContext = createContext(null);
export const useMaintenance = () => useContext(MaintenanceContext);

// Self-contained theme controller — same machinery as StockLayout so a
// maintenance-only user, who never mounts MainApp, gets the SAME appearance
// controls (light / auto / dark + Performance Mode), on the shared keys.
function applyThemePref(pref) {
  let dark = false;
  if (pref === "dark") dark = true;
  else if (pref === "system") { try { dark = window.matchMedia("(prefers-color-scheme: dark)").matches; } catch {} }
  const el = document.documentElement;
  el.classList.remove("light", "dark");
  el.classList.add(dark ? "dark" : "light");
}
function useMaintenanceTheme() {
  const [themePref, setPref] = useState(() => {
    try { const s = localStorage.getItem("rl_theme"); return ["light", "dark", "system"].includes(s) ? s : "system"; }
    catch { return "system"; }
  });
  const [flatMode, setFlat] = useState(() => {
    try { return localStorage.getItem("rl_flat") === "1"; } catch { return false; }
  });
  const prefRef = useRef(themePref);
  useEffect(() => { prefRef.current = themePref; }, [themePref]);
  useEffect(() => { applyThemePref(themePref); }, [themePref]);
  useEffect(() => {
    if (flatMode) document.documentElement.setAttribute("data-flat", "1");
    else document.documentElement.removeAttribute("data-flat");
  }, [flatMode]);
  useEffect(() => {
    if (themePref !== "system") return;
    let mq; const apply = () => applyThemePref("system");
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener?.("change", apply); } catch {}
    return () => { try { mq?.removeEventListener?.("change", apply); } catch {} };
  }, [themePref]);
  const setThemePref = useCallback((p) => { setPref(p); try { localStorage.setItem("rl_theme", p); } catch {} }, []);
  const cycleTheme = useCallback(() => {
    const order = ["light", "system", "dark"];
    const next = order[(order.indexOf(prefRef.current) + 1) % order.length];
    setThemePref(next); return next;
  }, [setThemePref]);
  const toggleFlat = useCallback(() => {
    setFlat((v) => { const next = !v; try { localStorage.setItem("rl_flat", next ? "1" : "0"); } catch {} return next; });
  }, []);
  return { themePref, setThemePref, cycleTheme, flatMode, toggleFlat };
}

const THEME_OPTIONS = [
  { key: "light",  icon: Sun,     label: "Light" },
  { key: "system", icon: Monitor, label: "Auto"  },
  { key: "dark",   icon: Moon,    label: "Dark"  },
];

function ThemeSlider({ themePref, setThemePref }) {
  return (
    <div className="px-3 pt-2 pb-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Appearance</p>
      <div className="rl-carved flex gap-0.5 p-0.5 rounded-full bg-[hsl(var(--muted))]/50" role="radiogroup" aria-label="Theme" data-testid="mt-theme-slider">
        {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
          <button key={key} type="button" role="radio" aria-checked={themePref === key}
            onClick={() => setThemePref(key)} data-testid={`mt-theme-${key}-btn`}
            className={`flex-1 flex items-center justify-center gap-1 h-6 rounded-full text-[11px] font-medium transition-colors ${
              themePref === key
                ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}>
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
      </div>
      {themePref === "system" && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5 text-center whitespace-nowrap">Follows your device setting</p>
      )}
    </div>
  );
}

function FlatModeRow({ flatMode, toggleFlat }) {
  return (
    <div className="px-3 pt-1.5 pb-2" data-testid="mt-flat-mode-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-[hsl(var(--foreground))]">
          <Gauge className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" /> Reduce visual effects
        </span>
        <Switch checked={flatMode} onCheckedChange={toggleFlat} aria-label="Reduce visual effects" data-testid="mt-flat-mode-switch" />
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 pl-6">Snappier on older devices</p>
    </div>
  );
}

const ROLE_BADGE = {
  Admin: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  Maintenance: "bg-[#2F6DAE]/15 text-[#2F6DAE] dark:text-[#7FB2E5] border-[#2F6DAE]/30",
};

// ── Nav row — the ERP spec verbatim: frosted Dashboard button, hairline
// divider, then the carved pipeline groove with the gliding chevron thumb.
// The groove is fit-content so the rail ENDS at Complete (locked, v3 mock).
const PIPE_OV = 11;

const EXTRA_ICON = { pm: CalendarClock, spares: Package, requests: ClipboardList };

function MaintenanceNav({ counts }) {
  const navigate = useNavigate();
  const location = useLocation();
  const thumbRef = useRef(null);
  const tabRefs = useRef(new Map());
  const mountedRef = useRef(false);
  const reduced = useMemo(() => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
  }, []);

  const activeIdx = MAINT_STAGES.findIndex((s) => location.pathname === s.path);
  const dashActive = activeIdx < 0;

  const place = useCallback((instant = false) => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    const idx = MAINT_STAGES.findIndex((s) => location.pathname === s.path);
    const stage = idx >= 0 ? MAINT_STAGES[idx] : null;
    const tab = stage ? tabRefs.current.get(stage.path) : null;
    if (!tab) { thumb.style.opacity = "0"; return; }
    const isFirst = idx === 0;
    const isLast = idx === MAINT_STAGES.length - 1;
    thumb.classList.toggle("pipe-thumb-first", isFirst);
    thumb.classList.toggle("pipe-thumb-last", isLast);
    const lo = isFirst ? 3 : PIPE_OV;
    const ro = isLast ? 3 : PIPE_OV;
    if (instant) thumb.style.transition = "none";
    thumb.style.opacity = "1";
    thumb.style.backgroundColor = stage.color;
    thumb.style.width = `${tab.offsetWidth + lo + ro}px`;
    thumb.style.transform = `translateX(${tab.offsetLeft - lo}px)`;
    if (instant) { void thumb.offsetHeight; thumb.style.transition = ""; }
  }, [location.pathname]);

  useLayoutEffect(() => {
    const instant = !mountedRef.current || reduced;
    mountedRef.current = true;
    place(instant);
  }, [place, reduced]);
  useEffect(() => {
    const onResize = () => place(true);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [place]);
  // Track the ACTIVE tab's own width — a count appears or changes without a
  // navigation, which changes the tab's width; re-fit the thumb instantly so a
  // count never pokes outside the colour.
  //
  // This MUST be a ResizeObserver, not an effect on [counts, place]: `place`
  // is rebuilt on every pathname change, so that effect re-ran on every
  // navigation and re-placed the thumb INSTANTLY — killing the glide. Same
  // lesson as the ERP's pipeline (Layout.js). And skip the observer's initial
  // callback: observe() fires once immediately, which on a navigation lands a
  // frame into the glide and would snap it just the same.
  useEffect(() => {
    const stage = MAINT_STAGES.find((x) => x.path === location.pathname);
    const tab = stage ? tabRefs.current.get(stage.path) : null;
    if (!tab || typeof ResizeObserver === "undefined") return undefined;
    let primed = false;
    const ro = new ResizeObserver(() => {
      if (!primed) { primed = true; return; }   // ignore the initial delivery
      place(true);
    });
    ro.observe(tab);
    return () => ro.disconnect();
  }, [location.pathname, place]);

  return (
    <nav className="border-b border-[hsl(var(--border))]" data-testid="maintenance-nav">
      <div className="max-w-[1440px] mx-auto px-3 md:px-6 pt-2 pb-3">
        <div className="flex items-center gap-2 gap-y-2 flex-wrap">
          <button
            onClick={() => navigate("/maintenance")}
            data-testid="mt-nav-dashboard"
            className={`inline-flex items-center justify-center md:justify-start gap-1.5 w-[42px] md:w-auto px-0 md:px-3.5 h-[42px] rounded-md text-[13px] font-semibold shrink-0 transition ${dashActive ? "text-white rl-dash-glass" : "rl-nav-glass text-[hsl(var(--muted-foreground))]"}`}
            style={{ fontFamily: "var(--rl-font-heading), sans-serif", ...(dashActive ? { backgroundColor: "#fbbf24", backgroundImage: "radial-gradient(60% 130% at 20% 30%, #a855f7 0%, transparent 55%), radial-gradient(70% 130% at 75% 25%, #ef4444 0%, transparent 55%), radial-gradient(80% 140% at 55% 95%, #ec4899 0%, transparent 55%), radial-gradient(70% 130% at 10% 90%, #38bdf8 0%, transparent 55%), radial-gradient(60% 120% at 90% 85%, #f59e0b 0%, transparent 55%)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" } : {}) }}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="hidden md:inline">Dashboard</span>
          </button>
          <div className="hidden md:block w-px self-center h-6 bg-[hsl(var(--border))] shrink-0" aria-hidden="true" />
          {/* Groove ends at Complete — no empty rail trailing after the last
              stage (locked 18-Aug, §8d′). */}
          <div className="pipeline-nav" style={{ width: "fit-content" }}>
            <div ref={thumbRef} className="pipe-thumb" aria-hidden="true" />
            {MAINT_STAGES.map((s, i) => {
              const isActive = i === activeIdx;
              const count = counts?.[s.key];
              return (
                <React.Fragment key={s.path}>
                  {i > 0 && (
                    <span className={`pipe-sep ${i === activeIdx || i - 1 === activeIdx ? "pipe-sep-hide" : ""}`} aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  )}
                  <div className="chevron-wrapper">
                    <button
                      ref={(el) => { if (el) tabRefs.current.set(s.path, el); else tabRefs.current.delete(s.path); }}
                      onClick={() => navigate(s.path)}
                      className={`pipe-tab ${isActive ? "active" : ""}`}
                      data-testid={`mt-nav-${s.key}`}
                      style={{ "--stage": s.color }}
                    >
                      <span>{s.label}</span>
                      {count !== undefined && count !== null && <span className="tab-count">{count}</span>}
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Phase 2 — the groove still ENDS at Complete. What grows after it
              is a hairline divider and three compact frosted buttons, pushed to
              the right edge so the pipeline keeps the left of the row to itself
              (locked 21-Aug, §0a.4). */}
          <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="w-px self-center h-6 bg-[hsl(var(--border))] shrink-0" aria-hidden="true" />
          {MAINT_EXTRAS.map((x) => {
            const Icon = EXTRA_ICON[x.key];
            const active = location.pathname === x.path;
            const count = counts?.[x.countKey] || 0;
            const alert = x.key === "pm" && count > 0;
            return (
              <button
                key={x.path}
                onClick={() => navigate(x.path)}
                data-testid={`mt-nav-${x.key}`}
                title={x.full}
                className={`inline-flex items-center justify-center gap-1.5 px-2.5 md:px-3 h-[42px] rounded-md text-[13px] font-semibold shrink-0 transition ${
                  active
                    ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                    : "rl-nav-glass text-[hsl(var(--muted-foreground))]"
                }`}
                style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}
              >
                <Icon className={`w-4 h-4 ${active ? "text-[#2F6DAE]" : ""}`} />
                <span className="hidden md:inline">{x.label}</span>
                {count > 0 && (
                  <span className={`rl-extra-count ${alert ? "is-alert" : ""}`} title={alert ? `${count} machine${count === 1 ? "" : "s"} overdue for service` : undefined}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default function MaintenanceLayout() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { themePref, setThemePref, cycleTheme, flatMode, toggleFlat } = useMaintenanceTheme();

  const [config, setConfig] = useState({ reasons: [], reason_other: "", can: {} });
  const [counts, setCounts] = useState(null);
  const [machineCount, setMachineCount] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, cnt] = await Promise.all([
          axios.get(`${API}/maintenance/config`),
          axios.get(`${API}/maintenance/counts`),
        ]);
        if (cancelled) return;
        setConfig(cfg.data || {});
        setCounts(cnt.data || null);
      } catch { /* the shell still renders; surfaces show their own states */ }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/maintenance/machines`)
      .then((r) => { if (!cancelled) setMachineCount(r.data?.total ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const initials = useMemo(() => {
    const n = (user?.name || user?.email || "?").trim();
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || n[0]?.toUpperCase() || "?";
  }, [user]);
  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];

  // Maintenance-only users (role "Maintenance") have no ERP footing and must
  // not be shown a way into it; everyone else here is dual-access.
  const isDualAccess = user?.role && user.role !== "Maintenance";

  useEffect(() => {
    const prev = document.title;
    document.title = "Maintenance — RockLink";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); cycleTheme(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cycleTheme]);

  // NOTE the user-id shape split (same trap as the division lens): the login
  // response carries `id`, /auth/me on every reload carries `user_id`. Accept
  // both or "Reported by mistake?" silently vanishes after a refresh.
  const userId = user?.id || user?.user_id || "";

  const ctx = useMemo(() => ({
    config, can: config?.can || {}, reasons: config?.reasons || [],
    reasonOther: config?.reason_other || "Something else…",
    counts, machineCount, refresh, refreshKey, userId, API,
  }), [config, counts, machineCount, refresh, refreshKey, userId]);

  return (
    <MaintenanceContext.Provider value={ctx}>
      <div className="min-h-screen rl-canvas" data-testid="maintenance-layout">
        <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/95 backdrop-blur">
          <div className="max-w-[1440px] mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3" data-testid="mt-brand">
              <span className="text-lg font-extrabold tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
              </span>
              <div className="hidden sm:flex flex-col pl-3 border-l border-[hsl(var(--border))]">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>Maintenance</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#2F6DAE]/12 text-[#2F6DAE] dark:text-[#7FB2E5] border border-[#2F6DAE]/30">
                    Machine Care
                  </span>
                </div>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] leading-tight mt-0.5">
                  Rockpecker Pvt Ltd · Bhateri plant{machineCount ? ` · ${machineCount} machines` : ""}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDualAccess && (
                <button
                  onClick={() => navigate("/")}
                  className="rl-frost-btn flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                  data-testid="mt-switch-to-erp"
                  title="Back to the main RockLink ERP"
                >
                  RockLink ERP <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full text-[hsl(var(--muted-foreground))] transition-[colors,box-shadow] duration-150 ${
                      menuOpen ? "bg-zinc-200 dark:bg-zinc-800 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]" : "rl-frost-btn"
                    }`}
                    data-testid="mt-user-menu" aria-label="User menu"
                  >
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#ef4444] to-[#f97316] text-white text-[10px] font-bold shrink-0">{initials}</span>
                    <span className="hidden sm:inline text-sm font-medium text-[hsl(var(--foreground))]">{firstName}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rl-frost">
                  <div className="px-3 py-2.5">
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{user?.name || user?.email}</p>
                    <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_BADGE[user?.role] || "bg-slate-500/15 text-slate-500 border-slate-500/30"}`}>
                      {user?.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <ThemeSlider themePref={themePref} setThemePref={setThemePref} />
                  <FlatModeRow flatMode={flatMode} toggleFlat={toggleFlat} />
                  <p className="px-3 pb-1 text-[10px] text-[hsl(var(--muted-foreground))]/70">Press <kbd className="font-mono-data">N</kbd> to cycle theme</p>
                  {isDualAccess && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/")}><ArrowRight className="w-4 h-4 mr-2" /> Go to RockLink ERP</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-[hsl(var(--destructive))]">Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <MaintenanceNav counts={counts} />

        <main className="max-w-[1440px] mx-auto px-4 py-5">
          <Outlet />
        </main>
      </div>
    </MaintenanceContext.Provider>
  );
}
